import { useState } from 'react'
import { VStack, HStack, Card, Text, Heading, Badge, Button, Skeleton, useToast } from 'wss3-forge'
import {
  DeviceMeetingRoom20Regular,
  Desktop20Regular,
  Phone20Regular,
  Tablet20Regular,
  Delete20Regular,
  DismissCircle20Regular,
  Add20Regular
} from '@fluentui/react-icons'
import type { TrustedDevice } from '../../types/database'

interface CurrentDeviceInfo {
  browser: string
  os: string
  deviceType: string
}

interface TrustedDevicesCardProps {
  devices: TrustedDevice[]
  loading: boolean
  onRevoke: (id: string) => Promise<{ error: Error | null }>
  onRevokeAll: () => Promise<{ error: Error | null }>
  onAddCurrentDevice?: () => Promise<{ error: Error | null; data?: TrustedDevice }>
  currentDeviceInfo?: CurrentDeviceInfo
}

const deviceIcons = {
  desktop: <Desktop20Regular />,
  mobile: <Phone20Regular />,
  tablet: <Tablet20Regular />
}

function formatDate(date: string): string {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)

  if (hours < 1) return 'Just now'
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString()
}

function getDaysUntilExpiry(expiresAt: string): number {
  const expires = new Date(expiresAt)
  const now = new Date()
  const diff = expires.getTime() - now.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function TrustedDevicesCard({
  devices,
  loading,
  onRevoke,
  onRevokeAll,
  onAddCurrentDevice,
  currentDeviceInfo
}: TrustedDevicesCardProps) {
  const toast = useToast()
  const [adding, setAdding] = useState(false)

  // Check if current device is already trusted
  const isCurrentDeviceTrusted = currentDeviceInfo && devices.some(
    d => d.browser === currentDeviceInfo.browser && d.os === currentDeviceInfo.os
  )

  const handleAddCurrentDevice = async () => {
    if (!onAddCurrentDevice) return
    setAdding(true)
    const { error } = await onAddCurrentDevice()
    if (error) {
      toast.error('Failed to add device')
    } else {
      toast.success('Device added as trusted')
    }
    setAdding(false)
  }

  const handleRevoke = async (device: TrustedDevice) => {
    if (!confirm(`Remove trust from "${device.device_name || 'Unknown device'}"? You'll need to log in again from this device.`)) return

    const { error } = await onRevoke(device.id)
    if (error) {
      toast.error('Failed to revoke device')
    } else {
      toast.success('Device trust removed')
    }
  }

  const handleRevokeAll = async () => {
    if (!confirm('Remove trust from all devices? You\'ll need to log in again on all devices.')) return

    const { error } = await onRevokeAll()
    if (error) {
      toast.error('Failed to revoke devices')
    } else {
      toast.success('All devices have been logged out')
    }
  }

  if (loading) {
    return (
      <Card padding="md">
        <VStack gap="md">
          <HStack gap="sm" style={{ alignItems: 'center' }}>
            <DeviceMeetingRoom20Regular style={{ color: 'var(--brand-primary)' }} />
            <Heading level={5}>Trusted Devices</Heading>
          </HStack>
          <VStack gap="sm">
            <Skeleton width="100%" height={60} />
            <Skeleton width="100%" height={60} />
          </VStack>
        </VStack>
      </Card>
    )
  }

  return (
    <Card padding="md">
      <VStack gap="md">
        <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
          <DeviceMeetingRoom20Regular style={{ color: 'var(--brand-primary)' }} />
          <Heading level={5}>Trusted Devices</Heading>
          {devices.length > 0 && (
            <Badge variant="default">{devices.length}</Badge>
          )}
        </HStack>

        <Text size="sm" color="muted">
          Trusted devices can stay logged in without requiring additional verification. Remove devices you don't recognize.
        </Text>

        {devices.length === 0 ? (
          <Card variant="outlined" padding="md" style={{ textAlign: 'center' }}>
            <VStack gap="sm" style={{ alignItems: 'center' }}>
              <DeviceMeetingRoom20Regular style={{ width: 32, height: 32, color: 'var(--text-muted)' }} />
              <Text color="muted">No trusted devices</Text>
              <Text size="sm" color="muted">
                Add this device as trusted to stay logged in
              </Text>
            </VStack>
          </Card>
        ) : (
          <VStack gap="sm">
            {devices.map((device) => {
              const daysLeft = getDaysUntilExpiry(device.expires_at)
              const isExpiringSoon = daysLeft <= 7

              return (
                <div
                  key={device.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '8px'
                  }}
                >
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <div style={{ color: 'var(--brand-primary)' }}>
                        {deviceIcons[device.device_type as keyof typeof deviceIcons] || <Desktop20Regular />}
                      </div>
                      <VStack gap="xs">
                        <Text size="sm" weight="medium">
                          {device.device_name || `${device.browser || 'Unknown'} on ${device.os || 'Unknown'}`}
                        </Text>
                        <HStack gap="sm">
                          <Text size="xs" color="muted">
                            Last used {formatDate(device.last_used_at)}
                          </Text>
                          {device.ip_address && (
                            <Text size="xs" color="muted">
                              {device.ip_address}
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Badge
                        size="sm"
                        variant={isExpiringSoon ? 'warning' : 'default'}
                      >
                        {daysLeft > 0 ? `${daysLeft}d left` : 'Expired'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Delete20Regular />}
                        onClick={() => handleRevoke(device)}
                      >{''}</Button>
                    </HStack>
                  </HStack>
                </div>
              )
            })}
          </VStack>
        )}

        {(onAddCurrentDevice && !isCurrentDeviceTrusted) || devices.length > 1 ? (
          <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
            {devices.length > 1 && (
              <Button
                variant="danger"
                size="sm"
                icon={<DismissCircle20Regular />}
                onClick={handleRevokeAll}
              >
                Revoke all
              </Button>
            )}
            {onAddCurrentDevice && !isCurrentDeviceTrusted && (
              <Button
                variant="secondary"
                size="sm"
                icon={<Add20Regular />}
                onClick={handleAddCurrentDevice}
                loading={adding}
              >
                Trust this device
              </Button>
            )}
          </HStack>
        ) : null}
      </VStack>
    </Card>
  )
}
